/** Display helpers for the clubs overview list (mockup-aligned). */
import { clubTracts } from '../map/utils/clubBoundaryRole';

export function getClubRoleKey(club) {
  if (club?.is_president) return 'owner';
  if (club?.current_user_role === 'member') return 'member';
  return 'member';
}

export function getClubRoleLabel(club) {
  if (club?.is_president) return 'Owner';
  if (club?.current_user_role === 'member') return 'Member';
  return '';
}

export function getStandCount(club) {
  return Array.isArray(club?.hunt_locations) ? club.hunt_locations.length : 0;
}

export function clubHasMapBoundary(club) {
  return clubTracts(club?.map?.hunt_boundaries).length > 0;
}

export function formatClubMetaLine(club) {
  if (club?.established_year) {
    return `Est. ${club.established_year}`;
  }
  if (club?.created) {
    return `Joined ${new Date(club.created).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    })}`;
  }
  return '';
}

export function computeClubsSummary(clubs) {
  const list = Array.isArray(clubs) ? clubs : [];
  let totalAcreage = 0;
  let hasAcreage = false;
  let totalMembers = 0;
  let totalStands = 0;
  let ownedCount = 0;

  list.forEach((club) => {
    totalMembers += Number(club.member_count) || 0;
    totalStands += getStandCount(club);
    if (club.is_president) ownedCount += 1;
    if (club.acreage != null && !Number.isNaN(Number(club.acreage))) {
      hasAcreage = true;
      totalAcreage += Number(club.acreage);
    }
  });

  return {
    clubCount: list.length,
    ownedCount,
    totalAcreage: hasAcreage ? totalAcreage : null,
    totalMembers,
    totalStands,
  };
}

/** Chrome kicker under CLUBS (e.g. "1 club registered / 1 owned"). */
export function formatClubsRegisteredKicker(clubCount, ownedCount) {
  const clubs = Number(clubCount) || 0;
  const owned = Number(ownedCount) || 0;
  return `${clubs} club${clubs === 1 ? '' : 's'} registered / ${owned} owned`;
}

export function filterClubsBySearch(clubs, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return clubs;
  return clubs.filter((club) => {
    const name = (club.name || '').toLowerCase();
    const desc = (club.description || '').toLowerCase();
    return name.includes(q) || desc.includes(q);
  });
}

/** Initials for club detail header avatar (handoff-style band). */
export function getClubInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

/**
 * Compact meta under club detail title: "1,840 ac · 14 members · 8 stands"
 * Omits zero stands; falls back when acreage missing.
 */
export function formatClubDetailShellMeta(club) {
  const parts = [];
  if (club?.acreage != null && !Number.isNaN(Number(club.acreage))) {
    parts.push(`${Number(club.acreage).toLocaleString()} ac`);
  }
  const members = club?.member_count ?? (Array.isArray(club?.members) ? club.members.length : null);
  if (members != null) {
    parts.push(`${members} member${Number(members) === 1 ? '' : 's'}`);
  }
  const stands = getStandCount(club);
  if (stands > 0) {
    parts.push(`${stands} stand${stands === 1 ? '' : 's'}`);
  }
  if (parts.length === 0 && club?.established_year) {
    return `Est. ${club.established_year}`;
  }
  return parts.join(' · ');
}
